/**
 * Chart SVG (RX/TX dBm vs tempo) extraído de App.tsx pra ser reusável
 * pela DeviceSignalTab (`/#device/<id>/signal`) e pela OpticalIfaceDetail
 * (`/#optical/<ifaceId>`). Sem dep de lib de chart.
 */
export function OpticalChart({
  series, rxLow, rxHigh, txLow, txHigh
}: {
  series: Array<{ ts: string; rxDbm: number | null; txDbm: number | null }>;
  rxLow: number | null; rxHigh: number | null;
  txLow: number | null; txHigh: number | null;
}) {
  if (!series.length) {
    return <div className="text-xs text-slate-500 py-6 text-center">sem dados ainda — aguarde 1 ciclo de coleta (60s)</div>;
  }
  const W = 800, H = 240, PAD = 28;
  const xs = series.map((_, i) => i);
  const allVals = series.flatMap((s) => [s.rxDbm, s.txDbm].filter((v): v is number => v != null && Number.isFinite(v)));
  const lows = [rxLow, txLow].filter((v): v is number => v != null);
  const highs = [rxHigh, txHigh].filter((v): v is number => v != null);
  let yMin = Math.min(...allVals, ...lows, -10);
  let yMax = Math.max(...allVals, ...highs, 5);
  yMin -= 2; yMax += 1;
  const x = (i: number) => PAD + (xs.length > 1 ? (i / (xs.length - 1)) * (W - PAD * 2) : 0);
  const y = (v: number) => H - PAD - ((v - yMin) / (yMax - yMin)) * (H - PAD * 2);
  const path = (vals: (number | null)[]) =>
    vals.map((v, i) => v == null ? null : `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).filter(Boolean).join(' ');
  const yTicks = 5;
  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 600 }}>
        <rect width={W} height={H} fill="#0f172a" />
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const v = yMin + ((yMax - yMin) * i) / yTicks;
          const ypx = y(v);
          return <g key={i}>
            <line x1={PAD} y1={ypx} x2={W - PAD} y2={ypx} stroke="#1e293b" strokeWidth="0.5" />
            <text x={4} y={ypx + 3} fill="#64748b" fontSize="10">{v.toFixed(0)}</text>
          </g>;
        })}
        {rxLow != null && <line x1={PAD} y1={y(rxLow)} x2={W - PAD} y2={y(rxLow)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="4 3" />}
        {rxHigh != null && <line x1={PAD} y1={y(rxHigh)} x2={W - PAD} y2={y(rxHigh)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="4 3" />}
        <path d={path(series.map((s) => s.rxDbm))} fill="none" stroke="#10b981" strokeWidth="1.5" />
        <path d={path(series.map((s) => s.txDbm))} fill="none" stroke="#0ea5e9" strokeWidth="1.5" />
        <rect x={W - PAD - 130} y={PAD - 18} width="130" height="14" fill="#0f172a" />
        <circle cx={W - PAD - 122} cy={PAD - 11} r="3" fill="#10b981" /><text x={W - PAD - 115} y={PAD - 8} fill="#94a3b8" fontSize="10">RX dBm</text>
        <circle cx={W - PAD - 70} cy={PAD - 11} r="3" fill="#0ea5e9" /><text x={W - PAD - 63} y={PAD - 8} fill="#94a3b8" fontSize="10">TX dBm</text>
      </svg>
      <div className="text-[10px] text-slate-500 text-center">RX em verde · TX em azul · linhas tracejadas âmbar = threshold RX</div>
    </div>
  );
}
